import {
  forwardRef,
  useMemo,
  useState,
  useCallback,
  useRef,
  useImperativeHandle
} from 'react'

import { tv } from 'tailwind-variants'

import { Icon } from '@/components/atoms'

export const inputVariants = tv({
  slots: {
    containerSlot: 'relative',
    inputSlot:
      'text-surface-white-400 placeholder:text-surface-white-400 placeholder:font-body border-surface-white-300 focus:border-surface-white-400 focus:ring-surface-white-400 focus:text-surface-black-50 bg-surface-white-50 font-body w-full appearance-none rounded-sm border px-4 font-normal outline-none ring-0 [appearance:textfield] hover:font-semibold [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
    leftIconSlot:
      'absolute bottom-0 left-3.5 top-0 flex items-center justify-center',
    rightIconSlot:
      'absolute bottom-0 right-3.5 top-0 flex items-center justify-center',
    actionTextSlot: 'text-surface-white-400 cursor-pointer'
  },
  variants: {
    size: {
      sm: {
        inputSlot: 'text-caption-sm h-10 py-2'
      },
      md: {
        inputSlot: 'text-body-md h-12 py-3'
      },
      lg: {
        inputSlot: 'text-body-lg h-14 py-4'
      }
    },
    error: {
      true: {
        inputSlot:
          'border-danger-100 text-danger-200 placeholder:text-danger-100 focus:border-danger-200 focus:ring-danger-100 font-semibold focus:ring-1',
        leftIconSlot: 'text-danger-200',
        rightIconSlot: 'text-danger-200'
      }
    },
    disabled: {
      true: {
        inputSlot:
          'bg-surface-white-200 text-surface-white-400 border-surface-white-300 cursor-not-allowed hover:!font-normal',
        leftIconSlot: 'text-surface-white-400',
        rightIconSlot: 'text-surface-white-400'
      }
    },
    readOnly: {
      true: {
        inputSlot:
          'bg-surface-white-300 text-surface-white-400 cursor-default font-bold hover:!font-bold'
      }
    },
    hasLeftAdornment: {
      true: {
        inputSlot: 'pl-10'
      }
    },
    hasRightAdornment: {
      true: {
        inputSlot: 'pr-12'
      }
    }
  },
  defaultVariants: {
    size: 'md'
  }
})

export type TInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'size'
> & {
  error?: string
  placeholder?: string
  className?: string
  size?: 'sm' | 'md' | 'lg'
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  disabled?: boolean
  readOnly?: boolean
  required?: boolean
  onEnter?: () => void
  helperTextId?: string
  errorTextId?: string
  loading?: boolean
  status?: 'success' | 'warning' | 'none'
  showPasswordToggle?: boolean
  showClearButton?: boolean
}

export const Input = forwardRef<HTMLInputElement, TInputProps>(
  (
    {
      placeholder,
      className,
      size,
      leftIcon,
      rightIcon,
      error,
      disabled,
      readOnly,
      required,
      onEnter,
      helperTextId,
      errorTextId,
      loading,
      status = 'none',
      showPasswordToggle,
      showClearButton,
      ...props
    },
    ref
  ) => {
    const {
      value: propValue,
      defaultValue: propDefaultValue,
      onChange: propOnChange,
      onInput: propOnInput,
      type: propType,
      name,
      id,
      ...restProps
    } = props

    const inputRef = useRef<HTMLInputElement>(null)
    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement)

    const [showPassword, setShowPassword] = useState(false)

    const isPassword = propType === 'password'
    const computedType = useMemo(() => {
      if (isPassword && showPasswordToggle) {
        return showPassword ? 'text' : 'password'
      }
      return propType
    }, [isPassword, showPassword, showPasswordToggle, propType])

    const describedBy = useMemo(() => {
      const ids = [helperTextId, errorTextId].filter(Boolean)
      return ids.length ? ids.join(' ') : undefined
    }, [helperTextId, errorTextId])

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (props.onKeyDown) props.onKeyDown(e)
        if (e.key === 'Enter' && onEnter) onEnter()
      },
      [props, onEnter]
    )

    const isControlled = propValue !== undefined
    const [uncontrolledValue, setUncontrolledValue] = useState<string>(() => {
      if (propDefaultValue === undefined || propDefaultValue === null) return ''
      if (Array.isArray(propDefaultValue as unknown))
        return (propDefaultValue as unknown[]).join(',')
      return String(propDefaultValue as unknown)
    })

    const currentValue = isControlled
      ? String((propValue as unknown) ?? '')
      : uncontrolledValue

    const canShowClear = useMemo(() => {
      const hasValue = currentValue.length > 0
      return !!showClearButton && hasValue && !disabled && !readOnly
    }, [showClearButton, currentValue, disabled, readOnly])

    const {
      containerSlot,
      inputSlot,
      leftIconSlot,
      rightIconSlot,
      actionTextSlot
    } = inputVariants({
      size,
      error: !!error,
      disabled: !!disabled,
      readOnly: !!readOnly,
      hasLeftAdornment: !!leftIcon,
      hasRightAdornment:
        !!rightIcon ||
        !!loading ||
        status !== 'none' ||
        !!showPasswordToggle ||
        !!canShowClear
    })

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!isControlled) setUncontrolledValue(e.target.value)
        if (propOnChange) propOnChange(e)
      },
      [isControlled, propOnChange]
    )

    const buildSyntheticEvent = useCallback(
      (nextValue: string, type: 'change' | 'input' = 'change') => {
        const target = (inputRef.current ||
          ({} as HTMLInputElement)) as HTMLInputElement
        const event: Partial<React.ChangeEvent<HTMLInputElement>> = {
          target: Object.assign(target, { value: nextValue, name, id }),
          currentTarget: Object.assign(target, { value: nextValue, name, id })
        }
        ;(event as React.ChangeEvent<HTMLInputElement>).type = type
        return event as React.ChangeEvent<HTMLInputElement>
      },
      [id, name]
    )

    return (
      <div className={containerSlot()}>
        <input
          className={inputSlot({ className })}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          aria-required={required}
          aria-invalid={!!error}
          aria-describedby={describedBy}
          aria-busy={loading ? true : undefined}
          type={computedType}
          ref={inputRef}
          onKeyDown={handleKeyDown}
          value={currentValue}
          onChange={handleChange}
          name={name}
          id={id || name}
          {...restProps}
        />
        {leftIcon && <div className={leftIconSlot()}>{leftIcon}</div>}
        {(rightIcon ||
          loading ||
          status !== 'none' ||
          showPasswordToggle ||
          canShowClear) && (
          <div className={rightIconSlot()}>
            {rightIcon}
            {showPasswordToggle && isPassword && (
              <button
                type='button'
                onMouseDown={e => e.preventDefault()}
                onClick={() => setShowPassword(s => !s)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                className={actionTextSlot()}
              >
                <Icon
                  name={showPassword ? 'visibility_off' : 'visibility'}
                  size='md'
                />
              </button>
            )}
            {canShowClear && (
              <button
                type='button'
                onMouseDown={e => e.preventDefault()}
                onClick={() => {
                  const nextValue = ''
                  if (inputRef.current) inputRef.current.value = nextValue
                  if (!isControlled) setUncontrolledValue(nextValue)
                  if (propOnInput)
                    propOnInput(
                      buildSyntheticEvent(
                        nextValue,
                        'input'
                      ) as React.ChangeEvent<HTMLInputElement>
                    )
                  if (propOnChange)
                    propOnChange(buildSyntheticEvent(nextValue, 'change'))
                }}
                aria-label='Clear input'
              >
                <span className={actionTextSlot()}>Clear</span>
              </button>
            )}
          </div>
        )}
      </div>
    )
  }
)

Input.displayName = 'Input'
